/**
 * Display helpers for TrialBalancePaginated.
 * Filter, sort, and Excel export request bodies stay in the trialBalance store.
 * Rows from gettrialbalance are raw numbers plus location_detail and reference_detail.
 */
import { TB_PAGINATED_FIELDS } from '../../../../utils/constants';

const NUMBER_KEYS = new Set(['opening_amount', 'transaction', 'audit_entries', 'closing_amount']);
const DATE_KEYS = new Set(['created_at', 'updated_at']);

export function emptyViewFilters() {
  return { groups: [], group_connectors: [] };
}

export function cloneViewFilters(filters) {
  if (!filters || !Array.isArray(filters.groups) || !filters.groups.length) {
    return emptyViewFilters();
  }
  return JSON.parse(JSON.stringify({
    groups: filters.groups,
    group_connectors: Array.isArray(filters.group_connectors) ? filters.group_connectors : [],
  }));
}

export function cloneViewSort(sort) {
  if (!Array.isArray(sort)) return [];
  return sort
    .filter((item) => item && item.field)
    .map((item) => ({
      field: item.field,
      direction: item.direction === 'desc' || item.direction === -1 ? 'desc' : 'asc',
    }));
}

function canonicalFilters(filters) {
  if (!filters || !Array.isArray(filters.groups)) return null;
  const groups = [];
  filters.groups.forEach((group) => {
    const conditions = (group && Array.isArray(group.conditions) ? group.conditions : [])
      .filter((condition) => condition && condition.column && condition.operator)
      .map((condition) => ({
        column: condition.column,
        operator: String(condition.operator).trim().toLowerCase(),
        value: condition.value === undefined ? null : condition.value,
      }));
    if (!conditions.length) return;
    groups.push({
      connector: String(group.connector || 'AND').toUpperCase(),
      conditions,
    });
  });
  if (!groups.length) return null;
  return {
    groups,
    group_connectors: Array.isArray(filters.group_connectors)
      ? filters.group_connectors.map((item) => String(item).toUpperCase())
      : [],
  };
}

function canonicalSort(sort) {
  const items = cloneViewSort(sort);
  return items.length ? items : null;
}

/**
 * Filters to send on the next page. Applied criteria win. When the response
 * classified the request as the saved default, keep that default so paging
 * still filters. A clear (empty request, not the saved view) stays empty.
 */
export function nextActiveFilters({ applied, defaults, requested, useSavedView }) {
  const appliedFilters = cloneViewFilters(applied);
  if (appliedFilters.groups.length) return appliedFilters;
  const defaultFilters = cloneViewFilters(defaults);
  if (useSavedView) return defaultFilters;
  if (JSON.stringify(canonicalFilters(requested)) === JSON.stringify(canonicalFilters(defaults))) {
    return defaultFilters;
  }
  return emptyViewFilters();
}

/**
 * Sort to send on the next page. Same rule as nextActiveFilters.
 * A system glcode sort is kept when the user has no saved sort.
 */
export function nextActiveSort({ applied, defaults, requested, useSavedView }) {
  const appliedSort = cloneViewSort(applied);
  const defaultSort = cloneViewSort(defaults);
  if (useSavedView) return defaultSort.length ? defaultSort : appliedSort;
  if (appliedSort.length) return appliedSort;
  if (JSON.stringify(canonicalSort(requested)) === JSON.stringify(canonicalSort(defaults))) {
    return defaultSort;
  }
  return [];
}

export function buildTbFilterFields(locationLabel) {
  const label = locationLabel || 'Location';
  return TB_PAGINATED_FIELDS.map((field) => (
    field.key === 'location' ? { ...field, label } : { ...field }
  ));
}

export function buildColumnHeaders(locationLabel) {
  const headers = [
    { value: 'indexno', text: 'SL No', sortable: false, filterable: false, visible: true },
  ];
  TB_PAGINATED_FIELDS.forEach((field) => {
    const header = {
      value: field.key,
      text: field.key === 'location' ? (locationLabel || field.label) : field.label,
      visible: true,
      filterable: field.filterable !== false,
      sortable: field.sortable !== false,
    };
    if (NUMBER_KEYS.has(field.key)) header.datatype = 'number';
    if (DATE_KEYS.has(field.key)) header.datatype = 'date';
    headers.push(header);
  });
  headers.push({
    value: 'action',
    text: 'Action',
    sortable: false,
    visible: true,
    datatype: 'action',
  });
  return headers;
}

export function locationNamesForRecord(record, locationNameById) {
  const details = Array.isArray(record && record.location_detail) ? record.location_detail : [];
  const fromDetail = details.map((entry) => entry && entry.name).filter(Boolean);
  if (fromDetail.length) return fromDetail;
  const map = locationNameById || {};
  const ids = Array.isArray(record && record.location)
    ? record.location
    : (record && record.location != null && record.location !== '' ? [record.location] : []);
  const names = [];
  ids.forEach((id) => {
    if (id == null || id === '') return;
    const key = typeof id === 'object' && id._id != null ? String(id._id) : String(id);
    if (map[key]) names.push(map[key]);
  });
  return names;
}

export function referenceList(record) {
  return Array.isArray(record && record.reference_detail) ? record.reference_detail : [];
}

const TB_FIELD_KEYS = new Set(TB_PAGINATED_FIELDS.map((field) => field.key));
const INDEX_COLUMN = 'indexno';

/**
 * Visible trial-balance field keys, in header order. indexno and action are display-only.
 */
export function headerKeysForSave(headers) {
  return (headers || [])
    .filter((header) => header && header.visible && TB_FIELD_KEYS.has(header.value))
    .map((header) => header.value);
}

/**
 * Visible allow-listed columns for the export workbook, in the current header order.
 * indexno and action are display-only and are not sent. Location uses the store header label.
 */
export function columnsForExport(headers, locationLabel) {
  return (headers || [])
    .filter((header) => header && header.visible && TB_FIELD_KEYS.has(header.value))
    .map((header) => ({
      field: header.value,
      label: header.value === 'location'
        ? (locationLabel || header.text || header.value)
        : (header.text || header.value),
    }));
}

/**
 * SL No stays first and visible. It is not part of the saved column layout.
 * @param {Array} headers
 * @returns {Array}
 */
export function withPinnedIndexColumn(headers) {
  const list = (headers || []).slice();
  const at = list.findIndex((header) => header && header.value === INDEX_COLUMN);
  if (at < 0) return list;
  const [indexColumn] = list.splice(at, 1);
  list.unshift({ ...indexColumn, visible: true });
  return list;
}

/**
 * Insert sourceKey immediately before targetKey.
 * Action stays last. SL No stays first. Neither is a drag source or a drop target.
 * Returns the original array when nothing moves.
 */
export function reorderHeaders(headers, sourceKey, targetKey) {
  if (!sourceKey || !targetKey || sourceKey === targetKey) return headers;
  if (sourceKey === 'action' || targetKey === 'action') return headers;
  if (sourceKey === INDEX_COLUMN || targetKey === INDEX_COLUMN) return headers;
  const list = (headers || []).slice();
  const from = list.findIndex((header) => header && header.value === sourceKey);
  if (from < 0) return headers;
  const [moved] = list.splice(from, 1);
  const to = list.findIndex((header) => header && header.value === targetKey);
  if (to < 0) return headers;
  list.splice(to, 0, moved);
  const actionAt = list.findIndex((header) => header && header.value === 'action');
  if (actionAt >= 0 && actionAt !== list.length - 1) {
    const [action] = list.splice(actionAt, 1);
    list.push(action);
  }
  return withPinnedIndexColumn(list);
}

/**
 * First-load saved view: show keys in the saved order and hide the other field columns.
 * SL No stays first and visible. Action stays last and visible. Unknown keys are ignored.
 */
export function applySavedHeaderKeys(headers, keys, locationLabel) {
  const order = (Array.isArray(keys) ? keys : []).filter((key) => TB_FIELD_KEYS.has(key));
  const byValue = {};
  (headers || []).forEach((header) => {
    if (header && header.value) byValue[header.value] = header;
  });
  const visible = new Set(order);
  const next = [];
  if (byValue.indexno) next.push({ ...byValue.indexno, visible: true });
  order.forEach((key) => {
    const header = byValue[key];
    if (!header) return;
    const copy = { ...header, visible: true };
    if (key === 'location' && locationLabel) copy.text = locationLabel;
    next.push(copy);
  });
  (headers || []).forEach((header) => {
    if (!header || header.value === 'indexno' || header.value === 'action') return;
    if (visible.has(header.value)) return;
    const copy = { ...header, visible: false };
    if (header.value === 'location' && locationLabel) copy.text = locationLabel;
    next.push(copy);
  });
  if (byValue.action) next.push({ ...byValue.action, visible: true });
  return next;
}
